import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentEmpresaId } from "@/integrations/supabase/auth-utils";
import { useInternationalization } from "@/contexts/InternationalizationContext";

interface Alerta {
  id: string;
  tipo: 'vencimento_hoje' | 'vencimento_proximo' | 'atrasado';
  titulo: string;
  descricao: string;
  data_vencimento: string;
  valor: number;
  dias_faltando?: number;
  origem: 'receber' | 'pagar';
}

export function AlertasFinanceiros() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { formatCurrency } = useInternationalization();

  const { data: alertas = [] } = useQuery({
    queryKey: ["alertas-financeiros"],
    queryFn: async (): Promise<Alerta[]> => {
      try {
        const empresaId = await getCurrentEmpresaId();
        if (!empresaId) return [];

        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);

        const em3Dias = new Date(hoje);
        em3Dias.setDate(em3Dias.getDate() + 3);

        // Buscar contas a receber
        const { data: contasReceber } = await supabase
          .from('contas_receber')
          .select('id, descricao, data_vencimento, valor_total, status')
          .eq('empresa_id', empresaId)
          .neq('status', 'cancelado')
          .neq('status', 'recebido');

        // Buscar contas a pagar
        const { data: contasPagar } = await supabase
          .from('contas_pagar')
          .select('id, descricao, data_vencimento, valor_total, status')
          .eq('empresa_id', empresaId)
          .neq('status', 'cancelado')
          .neq('status', 'pago');

        const alertasList: Alerta[] = [];

        // Processar contas a receber
        (contasReceber || []).forEach(conta => {
          const dataVenc = new Date(conta.data_vencimento);
          dataVenc.setHours(0, 0, 0, 0);
          const diasFaltando = Math.ceil((dataVenc.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));

          if (diasFaltando < 0) {
            alertasList.push({
              id: `receber-${conta.id}`,
              tipo: 'atrasado',
              titulo: `Recebimento Atrasado - ${Math.abs(diasFaltando)} dias`,
              descricao: conta.descricao,
              data_vencimento: conta.data_vencimento,
              valor: conta.valor_total,
              dias_faltando: diasFaltando,
              origem: 'receber'
            });
          } else if (diasFaltando === 0) {
            alertasList.push({
              id: `receber-${conta.id}`,
              tipo: 'vencimento_hoje',
              titulo: 'Vencimento Hoje - A Receber',
              descricao: conta.descricao,
              data_vencimento: conta.data_vencimento,
              valor: conta.valor_total,
              origem: 'receber'
            });
          } else if (diasFaltando <= 3) {
            alertasList.push({
              id: `receber-${conta.id}`,
              tipo: 'vencimento_proximo',
              titulo: `Vencimento em ${diasFaltando} dia${diasFaltando > 1 ? 's' : ''} - A Receber`,
              descricao: conta.descricao,
              data_vencimento: conta.data_vencimento,
              valor: conta.valor_total,
              dias_faltando: diasFaltando,
              origem: 'receber'
            });
          }
        });

        // Processar contas a pagar
        (contasPagar || []).forEach(conta => {
          const dataVenc = new Date(conta.data_vencimento);
          dataVenc.setHours(0, 0, 0, 0);
          const diasFaltando = Math.ceil((dataVenc.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));

          if (diasFaltando < 0) {
            alertasList.push({
              id: `pagar-${conta.id}`,
              tipo: 'atrasado',
              titulo: `Pagamento Atrasado - ${Math.abs(diasFaltando)} dias`,
              descricao: conta.descricao,
              data_vencimento: conta.data_vencimento,
              valor: conta.valor_total,
              dias_faltando: diasFaltando,
              origem: 'pagar'
            });
          } else if (diasFaltando === 0) {
            alertasList.push({
              id: `pagar-${conta.id}`,
              tipo: 'vencimento_hoje',
              titulo: 'Vencimento Hoje - A Pagar',
              descricao: conta.descricao,
              data_vencimento: conta.data_vencimento,
              valor: conta.valor_total,
              origem: 'pagar'
            });
          } else if (diasFaltando <= 3) {
            alertasList.push({
              id: `pagar-${conta.id}`,
              tipo: 'vencimento_proximo',
              titulo: `Vencimento em ${diasFaltando} dia${diasFaltando > 1 ? 's' : ''} - A Pagar`,
              descricao: conta.descricao,
              data_vencimento: conta.data_vencimento,
              valor: conta.valor_total,
              dias_faltando: diasFaltando,
              origem: 'pagar'
            });
          }
        });

        // Ordenar: atrasados primeiro, depois vencimento hoje, depois próximos
        return alertasList.sort((a, b) => {
          const ordem = { atrasado: 0, vencimento_hoje: 1, vencimento_proximo: 2 };
          return ordem[a.tipo] - ordem[b.tipo];
        });
      } catch (error) {
        console.error('Erro ao buscar alertas financeiros:', error);
        return [];
      }
    },
    refetchInterval: 60000, // Atualizar a cada minuto
  });

  const contarPorTipo = (tipo: Alerta['tipo']) => alertas.filter(a => a.tipo === tipo).length;
  const totalAtrasos = contarPorTipo('atrasado');
  const totalHoje = contarPorTipo('vencimento_hoje');
  const totalProximo = contarPorTipo('vencimento_proximo');

  const getCor = (tipo: Alerta['tipo']) => {
    switch (tipo) {
      case 'atrasado': return 'bg-red-50 border-red-200';
      case 'vencimento_hoje': return 'bg-orange-50 border-orange-200';
      case 'vencimento_proximo': return 'bg-yellow-50 border-yellow-200';
    }
  };

  const handleAlertaClick = (alerta: Alerta) => {
    setOpen(false);
    navigate(alerta.origem === 'receber' ? '/financeiro' : '/fluxo-caixa');
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="relative"
      >
        <Bell className="h-5 w-5" />
        {alertas.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
            {alertas.length}
          </span>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-96 overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Alertas Financeiros ({alertas.length})</DialogTitle>
          </DialogHeader>

          {alertas.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Nenhum alerta no momento</p>
            </div>
          ) : (
            <div className="space-y-3">
              {totalAtrasos > 0 && (
                <div>
                  <h3 className="font-semibold text-sm text-red-900 mb-2">
                    🔴 Atrasados ({totalAtrasos})
                  </h3>
                  {alertas.filter(a => a.tipo === 'atrasado').map(alerta => (
                    <div
                      key={alerta.id}
                      onClick={() => {
                        setOpen(false);
                        navigate(alerta.origem === 'receber' ? '/financeiro' : '/fluxo-caixa');
                      }}
                      className={`p-3 rounded-lg border ${getCor(alerta.tipo)} cursor-pointer transition-all hover:shadow-md hover:scale-[1.01]`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm text-gray-900">{alerta.titulo}</p>
                          <p className="text-xs text-gray-600">{alerta.descricao}</p>
                        </div>
                        <span className="font-semibold text-sm">{formatCurrency(alerta.valor)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {totalHoje > 0 && (
                <div>
                  <h3 className="font-semibold text-sm text-orange-900 mb-2">
                    Vencimento Hoje ({totalHoje})
                  </h3>
                  {alertas.filter(a => a.tipo === 'vencimento_hoje').map(alerta => (
                    <div
                      key={alerta.id}
                      onClick={() => handleAlertaClick(alerta)}
                      className={`p-3 rounded-lg border ${getCor(alerta.tipo)} cursor-pointer transition-all hover:shadow-md hover:scale-[1.01]`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm text-gray-900">{alerta.titulo}</p>
                          <p className="text-xs text-gray-600">{alerta.descricao}</p>
                        </div>
                        <span className="font-semibold text-sm">{formatCurrency(alerta.valor)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {totalProximo > 0 && (
                <div>
                  <h3 className="font-semibold text-sm text-yellow-900 mb-2">
                    Próximos ({totalProximo})
                  </h3>
                  {alertas.filter(a => a.tipo === 'vencimento_proximo').map(alerta => (
                    <div
                      key={alerta.id}
                      onClick={() => handleAlertaClick(alerta)}
                      className={`p-3 rounded-lg border ${getCor(alerta.tipo)} cursor-pointer transition-all hover:shadow-md hover:scale-[1.01]`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm text-gray-900">{alerta.titulo}</p>
                          <p className="text-xs text-gray-600">{alerta.descricao}</p>
                        </div>
                        <span className="font-semibold text-sm">{formatCurrency(alerta.valor)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
